import React from 'react'
import { useExperimentContext } from '@/experimentation'
import { ErrorBoundary, ErrorFallback } from '@/layout/ErrorBoundary'
import { SaleBanner } from '@/molecules/SaleBanner'
import { SaleBannerNew } from '@/molecules/SaleBannerNew'
import { TicketsNavBanner } from '@/molecules/TicketsNavBanner'
import { Footer } from '@/organisms/Footer'
import { SiteNav } from '@/organisms/SiteNav'
import { TheatricalReleaseRegion } from '@/types/codegen-federation'
import { useScreenType, ScreenType } from '@/utils/ScreenUtils'
import { BaseLayout } from '../BaseLayout'

export interface DefaultLayoutProps {
  children: React.ReactNode
  isDarkMode?: boolean
  siteNavGradient?: boolean
  siteNavTransparent?: boolean
  regions?: TheatricalReleaseRegion[]
  showFooter?: boolean
  hideNavigation?: boolean
  hideSaleBanner?: boolean
  hideTicketsNavBanner?: boolean
}

export const DefaultLayout: React.FC<DefaultLayoutProps> = ({
  children,
  isDarkMode = true,
  siteNavGradient = false,
  siteNavTransparent = false,
  regions,
  showFooter = true,
  hideNavigation = false,
  hideSaleBanner = false,
  hideTicketsNavBanner = false,
}) => {
  return (
    <BaseLayout isDarkMode={isDarkMode}>
      <DefaultLayoutContent
        regions={regions}
        siteNavGradient={siteNavGradient}
        siteNavTransparent={siteNavTransparent}
        hideNavigation={hideNavigation}
        hideSaleBanner={hideSaleBanner}
        hideTicketsNavBanner={hideTicketsNavBanner}
        showFooter={showFooter}
      >
        {children}
      </DefaultLayoutContent>
    </BaseLayout>
  )
}

const DefaultLayoutContent: React.FC<DefaultLayoutProps> = ({
  children,
  siteNavGradient = false,
  siteNavTransparent = false,
  regions,
  showFooter = true,
  hideNavigation = false,
  hideSaleBanner = false,
  hideTicketsNavBanner = false,
}) => {
  const screenType = useScreenType()
  const isDesktop = screenType === ScreenType.desktop
  const isMobile = screenType === ScreenType.mobile || screenType === ScreenType.mobileplus
  const showSaleBanner = !hideSaleBanner
  const { getFeatureValue } = useExperimentContext()

  // Only activate experiment that is correct based on device size
  const desktopVariantEnabled = isDesktop ? getFeatureValue('sale-banner-above-nav-desktop', false) : false
  const mobileVariantEnabled = isMobile ? getFeatureValue('sale-banner-above-nav-mobile', false) : false

  const belowNavBanner = React.useMemo(() => {
    if (!showSaleBanner) return null
    if (isDesktop) {
      return desktopVariantEnabled ? null : <SaleBanner />
    }
    if (isMobile) {
      return mobileVariantEnabled ? null : <SaleBanner />
    }
    return <SaleBanner />
  }, [desktopVariantEnabled, isDesktop, isMobile, mobileVariantEnabled, showSaleBanner])

  return (
    <>
      <TicketsNavBanner hide={hideTicketsNavBanner} />
      {showSaleBanner && isMobile && mobileVariantEnabled && (
        <div>
          <SaleBanner position="top" />
        </div>
      )}
      {showSaleBanner && isDesktop && desktopVariantEnabled && (
        <div>
          <SaleBannerNew />
        </div>
      )}
      <SiteNav
        hideNavigation={hideNavigation}
        regions={regions}
        gradientBackground={siteNavGradient}
        transparentBackground={siteNavTransparent}
      />
      {belowNavBanner}
      <ErrorBoundary FallbackComponent={ErrorFallback}>{children}</ErrorBoundary>
      {showFooter && <Footer />}
    </>
  )
}
